import {
  AddCircleOutline,
  BadgeOutlined,
  CheckCircleOutlined,
  ConfirmationNumberOutlined,
  DownloadRounded,
  ExpandMore,
  PersonOutlined,
  PhoneOutlined,
  ReceiptLongOutlined,
  RefreshRounded,
  SearchOutlined,
  ToggleOff,
  ToggleOn,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState, useEffect, useCallback, useRef } from 'react';
import RaffleService from '../../services/raffle';

const GRAD = 'linear-gradient(135deg, #7b4f9e 0%, #c0714a 100%)';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/* ── Generar lote ─────────────────────────────────────────────────────── */
const GenerateForm = ({ onGenerated, lotes = [] }) => {
  const [modo, setModo] = useState('nuevo'); // 'nuevo' | 'extender'
  const [form, setForm] = useState({ nombre_negocio: '', texto_evento: '', premio: '', monto_minimo: '', cantidad: '' });
  const [loteExt, setLoteExt] = useState('');
  const [cantidadExt, setCantidadExt] = useState('');
  const [loading, setLoading] = useState(false);
  const changed = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (modo === 'extender') {
        await RaffleService.generate({ lote: loteExt, cantidad: cantidadExt });
        setCantidadExt('');
      } else {
        await RaffleService.generate(form);
        setForm({ nombre_negocio: '', texto_evento: '', premio: '', monto_minimo: '', cantidad: '' });
      }
      onGenerated();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const canSubmit = modo === 'extender'
    ? Boolean(loteExt && cantidadExt)
    : Boolean(form.nombre_negocio);

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
      <Typography variant='subtitle2' fontWeight={700} mb={2} color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
        Gestión de lote
      </Typography>

      {/* Mode toggle */}
      <ToggleButtonGroup
        value={modo}
        exclusive
        onChange={(_, v) => v && setModo(v)}
        size='small'
        fullWidth
        sx={{ mb: 2, '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.82rem' },
              '& .Mui-selected': { background: `${alpha('#c0714a', 0.15)} !important`, color: '#c0714a !important', borderColor: `${alpha('#c0714a', 0.4)} !important` } }}
      >
        <ToggleButton value='nuevo'>Nuevo lote</ToggleButton>
        <ToggleButton value='extender' disabled={lotes.length === 0}>Agregar números a lote</ToggleButton>
      </ToggleButtonGroup>

      <Stack spacing={1.5}>
        {modo === 'extender' ? (
          <>
            <FormControl size='small' fullWidth>
              <InputLabel>Lote a extender</InputLabel>
              <Select value={loteExt} label='Lote a extender' onChange={(e) => setLoteExt(e.target.value)}>
                {lotes.map((l) => (
                  <MenuItem key={l.lote} value={l.lote}>
                    Lote {l.lote}{l.config?.nombre_negocio ? ` · ${l.config.nombre_negocio}` : ''}
                    {l.activo ? ' ✓' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size='small' type='number' label='Cantidad a agregar' fullWidth
              value={cantidadExt} onChange={(e) => setCantidadExt(e.target.value)}
              inputProps={{ min: 1 }}
            />
          </>
        ) : (
          <>
            <TextField size='small' label='Nombre del negocio' name='nombre_negocio' value={form.nombre_negocio} onChange={changed} fullWidth />
            <TextField size='small' label='Texto del evento' name='texto_evento' value={form.texto_evento} onChange={changed} fullWidth />
            <TextField size='small' label='Premio' name='premio' value={form.premio} onChange={changed} fullWidth />
            <TextField
              size='small' label='Monto mínimo de compra' name='monto_minimo' type='number'
              value={form.monto_minimo} onChange={changed} fullWidth
              helperText='Dejá en 0 o vacío para sin restricción'
              InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }}
              inputProps={{ min: 0 }}
            />
            <TextField
              size='small' label='Cantidad de números' name='cantidad' type='number'
              value={form.cantidad} onChange={changed} fullWidth
              helperText='Vacío = 100 por defecto'
              inputProps={{ min: 1 }}
            />
          </>
        )}

        <Button
          variant='contained'
          startIcon={loading ? <CircularProgress size='0.9rem' color='inherit' /> : <AddCircleOutline />}
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
          sx={{ borderRadius: 2, background: GRAD, '&:hover': { background: GRAD, filter: 'brightness(0.9)' } }}
        >
          {modo === 'extender' ? 'Agregar números' : 'Generar lote'}
        </Button>
      </Stack>
    </Paper>
  );
};

/* ── Detalle del lote (dialog) ────────────────────────────────────────── */
const LoteDetail = ({ lote, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async (p = 1) => {
    if (!lote) return;
    setLoading(true);
    try {
      const resp = await RaffleService.getLote(lote, { page: p, limit: 50 });
      setData((prev) => p === 1 ? resp : { ...resp, numbers: [...(prev?.numbers || []), ...resp.numbers] });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [lote]);

  useEffect(() => { if (open && lote) { setPage(1); setData(null); load(1); } }, [lote, open, load]);

  const handleLoadMore = () => { const n = page + 1; setPage(n); load(n); };

  const handleDownload = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      const resp = await RaffleService.downloadPdf(lote, {
        nombre: data.config?.nombre_negocio,
        evento: data.config?.texto_evento,
        premio: data.config?.premio,
      });
      downloadBlob(new Blob([resp.data], { type: 'application/pdf' }), `rifa_lote_${String(lote).padStart(4, '0')}.pdf`);
    } catch (err) { console.error(err); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ background: GRAD, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant='h6' sx={{ color: '#fff', fontWeight: 700 }}>
            Lote {lote}{data?.config?.nombre_negocio ? ` · ${data.config.nombre_negocio}` : ''}
          </Typography>
          <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.75)' }}>
            {data?.config?.texto_evento}{data?.config?.premio ? ` — Premio: ${data.config.premio}` : ''}
          </Typography>
        </Box>
        <Button
          size='small'
          startIcon={downloading ? <CircularProgress size='0.8rem' /> : <DownloadRounded />}
          onClick={handleDownload}
          disabled={downloading || loading || !data}
          sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', border: '1px solid', borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          PDF
        </Button>
      </Box>

      <DialogContent sx={{ p: 2, bgcolor: 'background.default' }}>
        {loading && !data && <Box py={4} display='flex' justifyContent='center'><CircularProgress sx={{ color: '#c0714a' }} /></Box>}
        {data && (
          <>
            <Box mb={2} p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box display='flex' justifyContent='space-between' mb={0.75}>
                <Typography variant='caption' fontWeight={700} color='text.secondary'>Vendidos</Typography>
                <Typography variant='caption' fontWeight={700}>{data.numbers?.filter(n => n.estado === 'vendido').length}/{data.total ?? data.numbers?.length}</Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={data.total ? (data.numbers?.filter(n => n.estado === 'vendido').length / data.total) * 100 : 0}
                sx={{ height: 7, borderRadius: 4, bgcolor: alpha('#c0714a', 0.15), '& .MuiLinearProgress-bar': { background: GRAD, borderRadius: 4 } }}
              />
            </Box>
            <Grid container spacing={0.75}>
              {(data.numbers || []).map((n) => (
                <Grid item xs={2} sm={1.2} key={n._id || n.numero}>
                  <Tooltip title={`${n.numero_formateado}${n.estado === 'vendido' ? ` · ${n.nombre_comprador || 'vendido'}` : ''}`}>
                    <Box sx={{
                      textAlign: 'center', py: 0.7, borderRadius: 1.5, border: '1px solid',
                      borderColor: n.estado === 'vendido' ? alpha('#2e7d32', 0.4) : 'divider',
                      bgcolor: n.estado === 'vendido' ? alpha('#2e7d32', 0.1) : 'transparent',
                      fontSize: '0.73rem', fontWeight: n.estado === 'vendido' ? 800 : 400,
                      color: n.estado === 'vendido' ? '#2e7d32' : 'text.secondary',
                      cursor: 'default',
                    }}>
                      {n.numero_formateado}
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
            {data.numbers?.length < (data.total ?? 0) && (
              <Box display='flex' justifyContent='center' mt={2}>
                <Button size='small' onClick={handleLoadMore} disabled={loading} sx={{ textTransform: 'none' }}>
                  {loading ? <CircularProgress size='0.9rem' /> : `Cargar más (${data.numbers.length}/${data.total})`}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ── Tab: Lotes ───────────────────────────────────────────────────────── */
const TabLotes = ({ lotes, loading, onDetail, onToggleActivo, togglingActivo }) => (
  <Box>
    {loading && <LinearProgress sx={{ mb: 1.5, borderRadius: 2 }} />}
    {!loading && lotes.length === 0 && (
      <Box py={6} textAlign='center'>
        <ConfirmationNumberOutlined sx={{ fontSize: 48, color: alpha('#c0714a', 0.3), mb: 1 }} />
        <Typography variant='body2' color='text.secondary'>No hay lotes generados aún</Typography>
      </Box>
    )}
    <Stack spacing={1.25}>
      {lotes.map((lote) => {
        const pct = lote.total > 0 ? Math.round(((lote.vendidos ?? 0) / lote.total) * 100) : 0;
        return (
          <Paper
            key={lote.lote}
            elevation={0}
            onClick={() => onDetail(lote.lote)}
            sx={{
              border: '1px solid',
              borderColor: lote.activo ? alpha('#c0714a', 0.45) : 'divider',
              borderRadius: 2.5, px: 2, py: 1.5,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: 3, borderColor: alpha('#c0714a', 0.6) },
              background: lote.activo ? `linear-gradient(90deg, ${alpha('#c0714a', 0.07)} 0%, transparent 100%)` : 'background.paper',
            }}
          >
            <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
              <Box flex={1} minWidth={0}>
                <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                  <Typography fontWeight={800} fontSize='0.95rem'>Lote {lote.lote}</Typography>
                  {lote.activo && <Chip size='small' label='Activo' sx={{ bgcolor: '#c0714a', color: '#fff', fontWeight: 700, fontSize: 10, height: 18 }} />}
                  {lote.config?.nombre_negocio && <Typography variant='caption' color='text.secondary' noWrap>{lote.config.nombre_negocio}</Typography>}
                </Box>
                {lote.config?.texto_evento && <Typography variant='caption' color='text.secondary'>{lote.config.texto_evento}</Typography>}
                <Box display='flex' alignItems='center' gap={1} mt={0.75}>
                  <LinearProgress
                    variant='determinate'
                    value={pct}
                    sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: alpha('#c0714a', 0.12), '& .MuiLinearProgress-bar': { background: GRAD, borderRadius: 3 } }}
                  />
                  <Typography variant='caption' color='text.secondary' sx={{ minWidth: 30, fontSize: 11 }}>{pct}%</Typography>
                </Box>
              </Box>
              <Box display='flex' alignItems='center' gap={0.5} onClick={(e) => e.stopPropagation()}>
                <Tooltip title={lote.activo ? 'Desactivar' : 'Activar'}>
                  <IconButton size='small' onClick={() => onToggleActivo(lote.lote, lote.activo)} disabled={togglingActivo === lote.lote}>
                    {togglingActivo === lote.lote
                      ? <CircularProgress size='1rem' />
                      : lote.activo ? <ToggleOn fontSize='small' sx={{ color: '#c0714a' }} /> : <ToggleOff fontSize='small' color='disabled' />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Stack>
  </Box>
);

/* ── Tab: Dashboard ───────────────────────────────────────────────────── */
const AsignadoRow = ({ item }) => {
  const isVendido = item.estado === 'vendido';
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: '52px 1fr auto',
      gap: 1.5, alignItems: 'center', p: 1.25, borderRadius: 2,
      border: '1px solid', borderColor: isVendido ? alpha('#2e7d32', 0.25) : 'divider',
      bgcolor: isVendido ? alpha('#2e7d32', 0.05) : 'background.paper',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isVendido ? 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)' : GRAD,
      }}>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.82rem' }}>{item.numero_formateado}</Typography>
      </Box>
      <Box minWidth={0}>
        <Box display='flex' alignItems='center' gap={0.5}>
          <PersonOutlined sx={{ fontSize: 13, color: 'text.secondary' }} />
          <Typography variant='body2' fontWeight={700} noWrap>{item.nombre_comprador || '—'}</Typography>
        </Box>
        <Box display='flex' gap={1.5} flexWrap='wrap' mt={0.2}>
          {item.dni && <Box display='flex' alignItems='center' gap={0.3}><BadgeOutlined sx={{ fontSize: 12, color: 'text.secondary' }} /><Typography variant='caption' color='text.secondary'>{item.dni}</Typography></Box>}
          {item.telefono && <Box display='flex' alignItems='center' gap={0.3}><PhoneOutlined sx={{ fontSize: 12, color: 'text.secondary' }} /><Typography variant='caption' color='text.secondary'>{item.telefono}</Typography></Box>}
        </Box>
      </Box>
      <Box display='flex' flexDirection='column' alignItems='flex-end' gap={0.5}>
        <Chip size='small' label={item.estado} sx={{ fontWeight: 700, fontSize: 11, bgcolor: isVendido ? alpha('#2e7d32', 0.1) : alpha('#9e9e9e', 0.1), color: isVendido ? '#2e7d32' : 'text.secondary' }} />
        {item.id_sale && (
          <Tooltip title={`Venta: ${item.id_sale}`}>
            <Box display='flex' alignItems='center' gap={0.3} sx={{ cursor: 'default' }}>
              <ReceiptLongOutlined sx={{ fontSize: 11, color: 'text.secondary' }} />
              <Typography variant='caption' color='text.secondary' sx={{ maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{item.id_sale}</Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const TabDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await RaffleService.getDashboard()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const lotes = data?.lotes || [];
  const pctGeneral = data?.total_general > 0 ? Math.round((data.vendidos_general / data.total_general) * 100) : 0;

  return (
    <Box>
      <Grid container spacing={1.25} mb={2}>
        {[
          { label: 'Total', val: data?.total_general ?? '—', color: '#7b4f9e' },
          { label: 'Vendidos', val: data?.vendidos_general ?? '—', color: '#2e7d32', sub: `${pctGeneral}%` },
          { label: 'Disponibles', val: data?.disponibles_general ?? '—', color: '#c0714a' },
        ].map(({ label, val, color, sub }) => (
          <Grid item xs={4} key={label}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha(color, 0.25), background: alpha(color, 0.07), textAlign: 'center' }}>
              <Typography variant='h5' sx={{ color, fontWeight: 800, lineHeight: 1 }}>{val}</Typography>
              {sub && <Typography variant='caption' sx={{ color, fontWeight: 600 }}>{sub}</Typography>}
              <Typography variant='caption' display='block' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10 }}>{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box display='flex' justifyContent='flex-end' mb={1}>
        <IconButton size='small' onClick={load} disabled={loading}>
          {loading ? <CircularProgress size={16} sx={{ color: '#c0714a' }} /> : <RefreshRounded fontSize='small' />}
        </IconButton>
      </Box>

      {loading && !data && <Box py={4} display='flex' justifyContent='center'><CircularProgress sx={{ color: '#c0714a' }} /></Box>}

      {lotes.map((lote) => {
        const pct = lote.total > 0 ? Math.round((lote.vendidos / lote.total) * 100) : 0;
        return (
          <Accordion key={lote.lote} elevation={0} disableGutters sx={{ mb: 1.25, border: '1px solid', borderColor: lote.activo ? alpha('#c0714a', 0.4) : 'divider', borderRadius: '12px !important', overflow: 'hidden', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2, py: 0.75, background: lote.activo ? alpha('#c0714a', 0.07) : 'transparent' }}>
              <Box display='flex' alignItems='center' gap={1.5} width='100%' mr={1}>
                <Box flex={1} minWidth={0}>
                  <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                    <Typography fontWeight={800} fontSize='0.93rem'>Lote {lote.lote}</Typography>
                    {lote.activo && <Chip size='small' label='Activo' sx={{ bgcolor: '#c0714a', color: '#fff', fontWeight: 700, fontSize: 10, height: 18 }} />}
                    {lote.config?.nombre_negocio && <Typography variant='caption' color='text.secondary' noWrap>{lote.config.nombre_negocio}</Typography>}
                  </Box>
                  <Box display='flex' alignItems='center' gap={1} mt={0.5}>
                    <LinearProgress variant='determinate' value={pct} sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: alpha('#c0714a', 0.12), '& .MuiLinearProgress-bar': { background: GRAD, borderRadius: 3 } }} />
                    <Typography variant='caption' color='text.secondary' sx={{ minWidth: 30, fontSize: 11 }}>{pct}%</Typography>
                  </Box>
                </Box>
                <Box display='flex' gap={0.75} flexShrink={0}>
                  <Chip size='small' label={`${lote.vendidos} vend.`} sx={{ bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32', fontWeight: 700, fontSize: 11 }} />
                  <Chip size='small' label={`${lote.disponibles} disp.`} sx={{ bgcolor: alpha('#7b4f9e', 0.1), color: '#7b4f9e', fontWeight: 700, fontSize: 11 }} />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1.5, bgcolor: 'background.default' }}>
              {lote.config?.premio && (
                <Box sx={{ mb: 1.5, px: 1.5, py: 0.9, borderRadius: 2, background: GRAD, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 600 }}>Premio:</Typography>
                  <Typography variant='body2' sx={{ color: '#fff', fontWeight: 700 }}>{lote.config.premio}</Typography>
                </Box>
              )}
              {!lote.asignados?.length ? (
                <Typography variant='body2' color='text.secondary' textAlign='center' py={1.5}>Sin números asignados aún</Typography>
              ) : (
                <Stack spacing={0.75}>
                  {lote.asignados.map((a) => <AsignadoRow key={a.numero} item={a} />)}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

/* ── Tab: Buscar ──────────────────────────────────────────────────────── */
const TabBuscar = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (value) => {
    if (!value.trim()) { setResults([]); setSearched(false); return; }
    setLoading(true);
    setSearched(true);
    try { setResults(await RaffleService.searchNumbers(value.trim())); }
    catch (err) { console.error(err); setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQ(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 420);
  };

  return (
    <Box>
      <TextField
        fullWidth
        size='small'
        placeholder='Buscar por nombre, DNI, teléfono o número...'
        value={q}
        onChange={handleChange}
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              {loading ? <CircularProgress size={16} sx={{ color: '#c0714a' }} /> : <SearchOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />}
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {!searched && !q && (
        <Box py={6} textAlign='center'>
          <SearchOutlined sx={{ fontSize: 48, color: alpha('#c0714a', 0.25), mb: 1 }} />
          <Typography variant='body2' color='text.secondary'>Escribí para buscar en todos los lotes</Typography>
        </Box>
      )}

      {searched && !loading && results.length === 0 && (
        <Box py={4} textAlign='center'>
          <Typography variant='body2' color='text.secondary'>Sin resultados para «{q}»</Typography>
        </Box>
      )}

      <Stack spacing={0.75}>
        {results.map((item) => {
          const isVendido = item.estado === 'vendido';
          return (
            <Box
              key={`${item.lote}-${item.numero}`}
              sx={{
                display: 'grid', gridTemplateColumns: '52px 1fr auto',
                gap: 1.5, alignItems: 'center', p: 1.25, borderRadius: 2,
                border: '1px solid', borderColor: isVendido ? alpha('#2e7d32', 0.25) : 'divider',
                bgcolor: isVendido ? alpha('#2e7d32', 0.05) : 'background.paper',
              }}
            >
              <Box sx={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isVendido ? 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)' : GRAD,
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.82rem' }}>{item.numero_formateado}</Typography>
              </Box>
              <Box minWidth={0}>
                <Box display='flex' alignItems='center' gap={0.75} flexWrap='wrap'>
                  <Box display='flex' alignItems='center' gap={0.4}>
                    <PersonOutlined sx={{ fontSize: 13, color: 'text.secondary' }} />
                    <Typography variant='body2' fontWeight={700} noWrap>{item.nombre_comprador || '—'}</Typography>
                  </Box>
                  <Chip size='small' label={`Lote ${item.lote}`} sx={{ fontSize: 10, height: 18, bgcolor: alpha('#7b4f9e', 0.1), color: '#7b4f9e', fontWeight: 700 }} />
                </Box>
                <Box display='flex' gap={1.5} flexWrap='wrap' mt={0.2}>
                  {item.dni && <Box display='flex' alignItems='center' gap={0.3}><BadgeOutlined sx={{ fontSize: 12, color: 'text.secondary' }} /><Typography variant='caption' color='text.secondary'>{item.dni}</Typography></Box>}
                  {item.telefono && <Box display='flex' alignItems='center' gap={0.3}><PhoneOutlined sx={{ fontSize: 12, color: 'text.secondary' }} /><Typography variant='caption' color='text.secondary'>{item.telefono}</Typography></Box>}
                </Box>
              </Box>
              <Box display='flex' flexDirection='column' alignItems='flex-end' gap={0.5}>
                <Chip size='small' label={item.estado} sx={{ fontWeight: 700, fontSize: 11, bgcolor: isVendido ? alpha('#2e7d32', 0.1) : alpha('#9e9e9e', 0.1), color: isVendido ? '#2e7d32' : 'text.secondary' }} />
                {item.id_sale && (
                  <Tooltip title={`Venta: ${item.id_sale}`}>
                    <Box display='flex' alignItems='center' gap={0.3} sx={{ cursor: 'default' }}>
                      <ReceiptLongOutlined sx={{ fontSize: 11, color: 'text.secondary' }} />
                      <Typography variant='caption' color='text.secondary' sx={{ maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{item.id_sale}</Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

/* ── Root ─────────────────────────────────────────────────────────────── */
const Rifa = ({ setOptions, setChildHeader }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);
  const [lotes, setLotes] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [detailLote, setDetailLote] = useState(null);
  const [togglingActivo, setTogglingActivo] = useState(null);

  const loadLotes = useCallback(async () => {
    setLoadingLotes(true);
    try { setLotes((await RaffleService.getLotes()) || []); }
    catch (err) { console.error(err); }
    finally { setLoadingLotes(false); }
  }, []);

  useEffect(() => {
    setOptions && setOptions(null);
    setChildHeader && setChildHeader(null);
    loadLotes();
  }, [loadLotes, setOptions, setChildHeader]);

  const handleToggleActivo = async (loteNum, currentActivo) => {
    setTogglingActivo(loteNum);
    try {
      await RaffleService.setActivo(loteNum, !currentActivo);
      setLotes((prev) => prev.map((l) => l.lote === loteNum ? { ...l, activo: !currentActivo } : l));
    } catch (err) { console.error(err); }
    finally { setTogglingActivo(null); }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ background: GRAD, px: { xs: 2, sm: 3 }, pt: 2.5, pb: 5, flexShrink: 0 }}>
        <Box display='flex' alignItems='center' gap={1.5}>
          <ConfirmationNumberOutlined sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 26 }} />
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.1 }}>
              Rifas
            </Typography>
            <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Gestión y seguimiento de lotes
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs — overlapping header */}
      <Box sx={{ mt: -3.5, mx: { xs: 1.5, sm: 3 }, zIndex: 1, flexShrink: 0 }}>
        <Paper elevation={2} sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant='fullWidth'
            sx={{
              minHeight: 42,
              bgcolor: 'background.paper',
              '& .MuiTab-root': { minHeight: 42, fontWeight: 700, fontSize: '0.82rem', textTransform: 'none' },
              '& .MuiTabs-indicator': { background: GRAD, height: 3, borderRadius: 2 },
            }}
          >
            <Tab label='Lotes' />
            <Tab label='Dashboard' />
            <Tab label='Buscar' />
            <Tab label='Nuevo lote' />
          </Tabs>
        </Paper>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, sm: 3 }, pt: 2, pb: 3 }}>
        {tab === 0 && (
          <TabLotes
            lotes={lotes}
            loading={loadingLotes}
            onDetail={(lote) => setDetailLote(lote)}
            onToggleActivo={handleToggleActivo}
            togglingActivo={togglingActivo}
          />
        )}
        {tab === 1 && <TabDashboard />}
        {tab === 2 && <TabBuscar />}
        {tab === 3 && <GenerateForm onGenerated={() => { loadLotes(); setTab(0); }} lotes={lotes} />}
      </Box>

      <LoteDetail lote={detailLote} open={Boolean(detailLote)} onClose={() => setDetailLote(null)} />
    </Box>
  );
};

export default Rifa;
